import React from 'react'
import type { CollectionSortType } from '@/api/collections'
import { useDantiAuth } from '@/hooks/use-danti-auth'
import {
  deleteRequest,
  getRequest,
  postRequest,
} from '@/utils/data-fetching/config'
import type { CollectionItem } from '@/utils/types/collection-types'
import { CategoryTypes } from '@/utils/types/result-types'
import * as Sentry from '@sentry/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { omitBy } from 'lodash'

export interface ListCollectionItemsParams {
  next?: string
  prev?: string
  limit?: number
  term?: string
  filter?: string
  sort?: CollectionSortType
}

export interface CollectionItemsResponse {
  items: CollectionItem[]
  next: string
  prev: string
  total: number
}

export interface AddItemParams {
  name: string
  desc: string
  owner: string
  tags: string[]
  collectionId: string
  preview: string
  refType: string
  refId: string
  doc: string
  category: string
  workspaceId: string
}

export interface DeleteItemParams {
  collectionId: string
  itemId: string
}

interface UseCollectionItemsHookResponse {
  items: CollectionItem[]
  totalCount: number
  isLoading: boolean
  pageSize: number
  currentPage: number
  selectedCategories: CategoryTypes[]
  filter?: string

  sort: CollectionSortType
  setSort: (sort: CollectionSortType) => void
  nextPage: () => void
  previousPage: () => void
  add: (params: AddItemParams) => Promise<void>
  remove: (params: DeleteItemParams) => Promise<void>
  search: (term: string) => void
  toggleSelectedCategory: (category: CategoryTypes) => void
}

async function list(
  collectionId: string,
  params: ListCollectionItemsParams,
): Promise<CollectionItemsResponse> {
  const response = await getRequest<CollectionItemsResponse>(
    `/collection/${collectionId}/items`,
    {
      params: omitBy(params, (value) => !value),
    },
  )

  return response.data
}

function add(params: AddItemParams) {
  return postRequest(`/collection/${params.collectionId}/items`, params)
}

function remove(params: DeleteItemParams) {
  const { collectionId, itemId } = params
  return deleteRequest(`/collection/${collectionId}/items/${itemId}`)
}

export function useCollectionItems(
  collectionId?: string,
): UseCollectionItemsHookResponse {
  const { isAuthenticated } = useDantiAuth()
  const [currentPage, setCurrentPage] = React.useState(0)
  const queryClient = useQueryClient()

  const [nextToken, setNextToken] = React.useState('')
  const [previousToken, setPreviousToken] = React.useState('')
  const [sort, setSort] = React.useState<CollectionSortType>('')
  const [searchTerm, setSearchTerm] = React.useState('')

  const [selectedCategories, setSelectedCategories] = React.useState<
    CategoryTypes[]
  >([
    CategoryTypes.IMAGE,
    CategoryTypes.PUBLICATION,
    CategoryTypes.SOCIAL_MEDIA,
    CategoryTypes.DATA,
    CategoryTypes.FILE,
    CategoryTypes.ANALYTIC,
  ])

  const filter = React.useMemo(() => {
    if (selectedCategories.length === 0) {
      return
    }
    return selectedCategories.join(',').toLowerCase()
  }, [selectedCategories])

  const pageSize = 50

  const { data, isLoading } = useQuery({
    queryKey: [
      'collections',
      collectionId,
      'items',
      nextToken,
      previousToken,
      sort,
      filter,
      searchTerm,
    ],
    queryFn: () =>
      list(collectionId!, {
        limit: pageSize,
        next: nextToken,
        prev: previousToken,
        term: searchTerm,
        sort,
        filter,
      }),
    enabled: isAuthenticated && !!collectionId,
    // TODO: Refetch more judiciously once Notification Service is online
    refetchInterval: 10000,
  })

  const totalCount = React.useMemo(() => data?.total ?? 0, [data])
  const maxPages = React.useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount],
  )

  function refreshCollection() {
    void queryClient.invalidateQueries({
      queryKey: ['collections'],
    })
  }

  const addMutation = useMutation({ mutationFn: add })
  const deleteMutation = useMutation({ mutationFn: remove })

  async function addItem(params: AddItemParams) {
    await addMutation.mutateAsync(params, {
      onSuccess: refreshCollection,
    })
  }

  async function removeItem(params: DeleteItemParams) {
    await deleteMutation.mutateAsync(params, {
      onSuccess: refreshCollection,
    })
  }

  const nextPage = () => {
    if (!data?.next) {
      return
    }

    setPreviousToken('')
    setNextToken(data.next)
    setCurrentPage((page) => Math.min(maxPages, page + 1))
  }

  const previousPage = () => {
    if (!data?.prev) {
      return
    }

    setNextToken('')
    setPreviousToken(data.prev)
    setCurrentPage((page) => Math.max(0, page - 1))
  }

  const search = React.useCallback((term: string) => {
    setNextToken('')
    setPreviousToken('')
    setCurrentPage(0)
    setSearchTerm(term)
  }, [])

  const toggleSelectedCategory = (category: CategoryTypes) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories((selectedCategories) =>
        [...selectedCategories].filter((item) => item != category),
      )
    } else {
      setSelectedCategories((selectedCategories) => [
        ...selectedCategories,
        category,
      ])
    }
  }

  return {
    items: data?.items ?? [],
    totalCount,
    isLoading,
    pageSize,
    currentPage,
    sort,
    filter,
    selectedCategories,
    setSort,
    nextPage,
    previousPage,
    add: addItem,
    remove: removeItem,
    search,
    toggleSelectedCategory,
  }
}

export const downloadFile = (endpoint: string, fileName: string) => {
  void getRequest(endpoint)
    .then((response) => {
      const fileURL = response.data as string
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = fileURL
      a.download = fileName
      document.body.append(a)
      a.click()
      window.URL.revokeObjectURL(fileURL)
      a.remove()
    })
    .catch(() => {
      Sentry.captureMessage('Failed to download extents', {
        extra: {
          endpoint: endpoint,
          ...(fileName && { fileName }),
        },
      })
    })
}
